// src/pages/DraftPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../firebase";
import { players as allPlayers } from "../players";
import { ref, onValue, get, set, update, runTransaction } from "firebase/database";

const ROLE_LIMITS = { BAT: 7, BOWL: 7, ALR: 5, WK: 4 };
const MAX_SQUAD = 15;
const TEAM_SLOTS = [
  "Team 1","Team 2","Team 3","Team 4","Team 5","Team 6",
  "Team 7","Team 8","Team 9","Team 10","Team 11","Team 12"
];
const ROLE_OPTIONS = ["ALL", "BAT", "BOWL", "ALR", "WK"];
const EMPTY_OBJ = {};
const EMPTY_ARR = [];
const EMPTY_PICKS = [];

function makeClientId() {
  const key = "fantasy_ipl_client_id";
  let v = localStorage.getItem(key);
  if (!v) {
    v = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(key, v);
  }
  return v;
}
function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function currentPickTeamName(order, round, pickInRound) {
  const teamCount = order.length;
  const idx = round % 2 === 1 ? pickInRound - 1 : teamCount - pickInRound;
  return order[idx] || order[0];
}
function nextPickState(order, round, pickInRound) {
  const teamCount = order.length;
  let nextRound = round;
  let nextPick = pickInRound + 1;
  if (nextPick > teamCount) {
    nextRound = round + 1;
    nextPick = 1;
  }
  const nextTeam = currentPickTeamName(order, nextRound, nextPick);
  return { round: nextRound, pickInRound: nextPick, onTheClockTeam: nextTeam };
}
function getOrderForTeamCount(order, teamCount) {
  const fallback = TEAM_SLOTS.slice(0, teamCount);
  if (!Array.isArray(order) || order.length !== teamCount) return fallback;
  const allowed = new Set(fallback);
  const filtered = order.filter((x) => allowed.has(x));
  if (filtered.length !== teamCount) return fallback;
  const uniq = new Set(filtered);
  if (uniq.size !== teamCount) return fallback;
  return filtered;
}
function isMobileNow() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(max-width: 900px)").matches;
}

export function DraftPage({ leagueCode }) {
  const user = auth.currentUser;
  const generatedClientId = useMemo(() => makeClientId(), []);
  const clientId = user?.uid ?? generatedClientId;
  const effectiveDisplayName =
    (user?.displayName && user.displayName.trim()) ||
    (user?.email ? user.email.split("@")[0] : "");

  const [displayName, setDisplayName] = useState(effectiveDisplayName || "");
  const [teamSlot, setTeamSlot] = useState(TEAM_SLOTS[0]);
  const [joined, setJoined] = useState(false);
  const [status, setStatus] = useState("");
  const [seasonStatus, setSeasonStatus] = useState("");

  const [roomData, setRoomData] = useState(null);
  const [previewRoom, setPreviewRoom] = useState(null);
  const [activeLeague, setActiveLeague] = useState(null);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [needOnly, setNeedOnly] = useState(false);
  const [sortMode, setSortMode] = useState("AZ");

  const [hostTeamCountInput, setHostTeamCountInput] = useState(6);
  const [hostTimerInput, setHostTimerInput] = useState(60);

  const [nowTick, setNowTick] = useState(Date.now());
  const [collapsedTeams, setCollapsedTeams] = useState(() => (isMobileNow() ? {} : {}));
  const [historyOpen, setHistoryOpen] = useState(() => !isMobileNow());

  const initialPlayersObj = useMemo(() => {
    const obj = {};
    for (const p of allPlayers) {
      obj[String(p.id)] = { id: p.id, name: p.name, role: p.role, team: p.team, drafted: false, owner: "" };
    }
    return obj;
  }, []);

  useEffect(() => {
    const code = (leagueCode || "").trim().toUpperCase();
    if (!code) return;
    const unsubL = onValue(ref(db, `leagues/${code}`), (snap) => setActiveLeague(snap.val() || null));
    const unsubR = onValue(ref(db, `rooms/${code}`), (snap) => setPreviewRoom(snap.val() || null));
    return () => { unsubL(); unsubR(); };
  }, [leagueCode]);

  useEffect(() => {
    if (!joined) return;
    const code = (leagueCode || "").trim().toUpperCase();
    if (!code) return;
    const unsub = onValue(ref(db, `rooms/${code}`), (snap) => setRoomData(snap.val() || null));
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => { unsub(); clearInterval(t); };
  }, [joined, leagueCode]);

  useEffect(() => {
    if (effectiveDisplayName) setDisplayName(effectiveDisplayName);
  }, [effectiveDisplayName]);

  const teamCount = roomData?.settings?.teamCount ?? previewRoom?.settings?.teamCount ?? activeLeague?.settings?.teamCount ?? 6;
  const timerSeconds = roomData?.settings?.timerSeconds ?? previewRoom?.settings?.timerSeconds ?? activeLeague?.settings?.timerSeconds ?? 60;
  const started = !!roomData?.settings?.started;
  const paused = !!roomData?.settings?.paused;
  const allowAutoPick = roomData?.settings?.allowAutoPick ?? true;

  const activeSlots = useMemo(() => TEAM_SLOTS.slice(0, teamCount), [teamCount]);
  const previewSlots = useMemo(() => previewRoom?.slots ?? EMPTY_OBJ, [previewRoom?.slots]);
  const getPreviewSlotStatus = useMemo(() => (slotId) => previewSlots[slotId] || null, [previewSlots]);

  useEffect(() => {
    if (joined) return;
    const active = activeSlots;
    if (!active.includes(teamSlot)) {
      const firstOpen = active.find((s) => !getPreviewSlotStatus(s));
      setTeamSlot(firstOpen || active[0]);
      return;
    }
    const cur = getPreviewSlotStatus(teamSlot);
    if (cur && cur.claimedBy && cur.claimedBy !== clientId) {
      const firstOpen = active.find((s) => !getPreviewSlotStatus(s));
      if (firstOpen) setTeamSlot(firstOpen);
    }
  }, [joined, activeSlots, teamSlot, clientId, getPreviewSlotStatus]);

  const draftOrder = useMemo(() => {
    const raw = roomData?.settings?.draftOrder;
    return getOrderForTeamCount(raw, teamCount);
  }, [roomData?.settings?.draftOrder, teamCount]);

  const isHost = roomData?.hostId === clientId;
  const slotLabel = (slotId) => {
    const custom = roomData?.teamCustom?.[slotId]?.name;
    const slot   = roomData?.slots?.[slotId]?.name;
    const n = custom || slot;
    const cleaned = typeof n === "string" ? n.trim() : "";
    return cleaned ? cleaned : slotId;
  };

  const participants = useMemo(() => {
    const s = roomData?.slots;
    if (!s) return EMPTY_ARR;
    const list = Object.entries(s).map(([slotName, v]) => ({ slotName, ...v }));
    return list.filter((p) => activeSlots.includes(p.slotName));
  }, [roomData?.slots, activeSlots]);
  const claimedCount = participants.length;
  const readyCount = participants.filter((p) => p.ready).length;

  const slotInfo = useMemo(() => {
    const s = roomData?.slots;
    return s ? s[teamSlot] : undefined;
  }, [roomData?.slots, teamSlot]);
  const iAmInSlot = slotInfo?.claimedBy === clientId;

  const playersArray = useMemo(() => {
    const p = roomData?.players;
    return p ? Object.values(p) : EMPTY_ARR;
  }, [roomData?.players]);
  const playersObj = roomData?.players ?? EMPTY_OBJ;
  const playerById = useMemo(() => {
    const m = new Map();
    for (const p of playersArray) m.set(String(p.id), p);
    return m;
  }, [playersArray]);

  const teams = roomData?.teams || activeSlots.map((name) => ({ name, squadIds: [] }));
  const picks = roomData?.picks ?? EMPTY_PICKS;
  const overallPick = roomData?.state?.overallPick ?? 1;
  const round = roomData?.state?.round ?? 1;
  const pickInRound = roomData?.state?.pickInRound ?? 1;
  const expectedTeam = useMemo(
    () => currentPickTeamName(draftOrder, round, pickInRound),
    [draftOrder, round, pickInRound]
  );
  const clockEndsAt = roomData?.state?.clockEndsAt ?? 0;
  const secondsLeft = useMemo(() => {
    if (!started || paused || !clockEndsAt) return null;
    return Math.ceil((clockEndsAt - nowTick) / 1000);
  }, [started, paused, clockEndsAt, nowTick]);

  const myTeam = useMemo(() => teams.find((t) => t.name === teamSlot) || null, [teams, teamSlot]);
  const myCounts = useMemo(() => {
    const counts = { BAT: 0, BOWL: 0, ALR: 0, WK: 0 };
    const ids = myTeam?.squadIds || EMPTY_ARR;
    for (const id of ids) {
      const p = playerById.get(String(id)) || playersObj[String(id)];
      if (!p) continue;
      if (counts[p.role] !== undefined) counts[p.role] += 1;
    }
    return counts;
  }, [myTeam, playerById, playersObj]);
  const myDeficits = useMemo(() => ({
    BAT: ROLE_LIMITS.BAT - myCounts.BAT,
    BOWL: ROLE_LIMITS.BOWL - myCounts.BOWL,
    ALR: ROLE_LIMITS.ALR - myCounts.ALR,
    WK: ROLE_LIMITS.WK - myCounts.WK
  }), [myCounts]);
  const neededRolesSorted = useMemo(() => (
    Object.keys(myDeficits).map((role) => ({ role, deficit: myDeficits[role] }))
      .filter((x) => x.deficit > 0).sort((a, b) => b.deficit - a.deficit)
  ), [myDeficits]);

  const filteredPlayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = playersArray
      .filter((p) => !p.drafted)
      .filter((p) => roleFilter === "ALL" || p.role === roleFilter)
      .filter((p) => (q ? p.name.toLowerCase().includes(q) : true));
    if (needOnly && iAmInSlot) list = list.filter((p) => (myDeficits[p.role] ?? 0) > 0);
    const cmpAZ = (a, b) => a.name.localeCompare(b.name);
    const cmpRole = (a, b) => (a.role === b.role ? cmpAZ(a, b) : a.role.localeCompare(b.role));
    const cmpNeed = (a, b) => {
      const da = myDeficits[a.role] ?? 0;
      const dbb = myDeficits[b.role] ?? 0;
      if (da === dbb) return cmpAZ(a, b);
      return dbb - da;
    };
    if (sortMode === "ROLE") list = list.sort(cmpRole);
    else if (sortMode === "NEED") list = list.sort(cmpNeed);
    else list = list.sort(cmpAZ);
    return list;
  }, [playersArray, search, roleFilter, needOnly, sortMode, iAmInSlot, myDeficits]);

  const history = useMemo(() => {
    const arr = [...(picks || EMPTY_PICKS)];
    arr.sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
    return arr.slice(0, 20);
  }, [picks]);

  function computeTeamCounts(team) {
    const counts = { BAT: 0, BOWL: 0, ALR: 0, WK: 0 };
    const squadIds = team?.squadIds || EMPTY_ARR;
    for (const id of squadIds) {
      const pl = playerById.get(String(id));
      if (!pl) continue;
      if (counts[pl.role] !== undefined) counts[pl.role] += 1;
    }
    return { counts, total: squadIds.length };
  }

  const enforceOwnerAsHost = async (code) => {
    try {
      const lgSnap = await get(ref(db, `leagues/${code}`));
      if (!lgSnap.exists()) return;
      const ownerUid = lgSnap.val()?.ownerUid;
      if (!ownerUid) return;
      const roomSnap = await get(ref(db, `rooms/${code}`));
      if (!roomSnap.exists()) return;
      const room = roomSnap.val();
      if (room?.settings?.started) return;
      if (room?.hostId !== ownerUid) {
        await update(ref(db, `rooms/${code}`), { hostId: ownerUid });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const joinRoom = async () => {
    const code = leagueCode.trim().toUpperCase();
    const dn = displayName.trim() || effectiveDisplayName || "Player";
    if (!dn) return setStatus("Enter your name.");
    setStatus("Joining...");
    try {
      // Create room if it doesn't exist
      const snap = await get(ref(db, `rooms/${code}`));
      if (!snap.exists()) {
        const lg = await get(ref(db, `leagues/${code}`));
        if (!lg.exists()) return setStatus("Room/League not found.");
        const ownerUid = lg.val()?.ownerUid;
        const tc = lg.val()?.settings?.teamCount ?? 6;
        const ts = lg.val()?.settings?.timerSeconds ?? 60;
        const initialOrder = TEAM_SLOTS.slice(0, tc);
        await set(ref(db, `rooms/${code}`), {
          createdAt: Date.now(),
          hostId: ownerUid || clientId,
          settings: { teamCount: tc, timerSeconds: ts, started: false, paused: false, allowAutoPick: true, draftOrder: initialOrder },
          slots: {},
          players: initialPlayersObj,
          teams: TEAM_SLOTS.slice(0, tc).map((n) => ({ name: n, squadIds: [] })),
          picks: [],
          state: { overallPick: 1, round: 1, pickInRound: 1, onTheClockTeam: initialOrder[0], clockEndsAt: 0 }
        });
      }
      await enforceOwnerAsHost(code);

      // Auto-assign to next open slot using a transaction
      let assignedSlot = null;
      await runTransaction(ref(db, `rooms/${code}`), (room) => {
        if (!room) return room;
        room.slots = room.slots || {};

        // Check if already joined
        const existing = Object.entries(room.slots).find(([, v]) => v?.claimedBy === clientId);
        if (existing) { assignedSlot = existing[0]; return room; }

        const tc = room.settings?.teamCount ?? 6;
        const active = TEAM_SLOTS.slice(0, tc);

        // Find next open slot
        const openSlot = active.find(s => !room.slots[s]?.claimedBy);
        if (!openSlot) { return undefined; } // abort: room full

        room.slots[openSlot] = { claimedBy: clientId, name: dn, ready: false, joinedAt: Date.now() };
        assignedSlot = openSlot;
        return room;
      });

      if (!assignedSlot) return setStatus("Draft room is full — no open slots.");
      setTeamSlot(assignedSlot);
      setJoined(true);
      setStatus(`Joined as ${dn} · assigned to ${assignedSlot}.`);
    } catch (e) {
      console.error(e);
      setStatus("Join failed: " + (e?.message || "unknown error"));
    }
  };

  const toggleReady = async () => {
    const code = leagueCode.trim().toUpperCase();
    if (!iAmInSlot) return setStatus("You must claim this slot to ready up.");
    try {
      await runTransaction(ref(db, `rooms/${code}`), (room) => {
        if (!room?.slots?.[teamSlot]) return room;
        const cur = room.slots[teamSlot];
        if (cur.claimedBy !== clientId) return room;
        room.slots[teamSlot] = { ...cur, ready: !cur.ready };
        return room;
      });
    } catch (e) {
      console.error(e);
      setStatus("Failed to toggle ready.");
    }
  };

  const hostSetTeamCount = async (n) => {
    const code = leagueCode.trim().toUpperCase();
    if (!isHost || started) return;
    const next = clamp(Number(n), 2, 12);
    const nextOrder = TEAM_SLOTS.slice(0, next);
    try {
      await update(ref(db, `rooms/${code}`), {
        "settings/teamCount": next,
        "settings/draftOrder": nextOrder,
        teams: TEAM_SLOTS.slice(0, next).map((name) => ({ name, squadIds: [] })),
        picks: [],
        state: { overallPick: 1, round: 1, pickInRound: 1, onTheClockTeam: nextOrder[0], clockEndsAt: 0 }
      });
      setStatus(`Team count set to ${next}.`);
    } catch (e) { setStatus("Failed to update team count."); }
  };

  const hostSetTimer = async (n) => {
    const code = leagueCode.trim().toUpperCase();
    if (!isHost) return;
    const next = clamp(Number(n), 10, 300);
    try {
      await update(ref(db, `rooms/${code}`), { "settings/timerSeconds": next });
      setStatus(`Timer set to ${next}s.`);
    } catch { setStatus("Failed to update timer."); }
  };

  const hostReshuffleOrder = async () => {
    const code = leagueCode.trim().toUpperCase();
    if (!isHost || started) return;
    try {
      const shuffled = shuffleArray(TEAM_SLOTS.slice(0, teamCount));
      await update(ref(db, `rooms/${code}`), {
        "settings/draftOrder": shuffled,
        "state/onTheClockTeam": shuffled[0],
        "state/round": 1,
        "state/pickInRound": 1,
        "state/overallPick": 1,
        "state/clockEndsAt": 0
      });
      setStatus("Draft order reshuffled.");
    } catch { setStatus("Failed to reshuffle order."); }
  };

  const hostStartDraft = async () => {
    const code = leagueCode.trim().toUpperCase();
    if (!isHost) return;
    if (claimedCount < teamCount) return setStatus(`Need ${teamCount} claimed slots before starting.`);
    try {
      const now = Date.now();
      const shuffled = shuffleArray(TEAM_SLOTS.slice(0, teamCount));
      await update(ref(db, `rooms/${code}`), {
        "settings/started": true,
        "settings/paused": false,
        "settings/draftOrder": shuffled,
        "state/overallPick": 1,
        "state/round": 1,
        "state/pickInRound": 1,
        "state/onTheClockTeam": shuffled[0],
        "state/clockEndsAt": now + timerSeconds * 1000
      });
      setStatus("Draft started!");
    } catch { setStatus("Failed to start draft."); }
  };

  const hostTogglePause = async () => {
    const code = leagueCode.trim().toUpperCase();
    if (!isHost) return;
    try {
      await runTransaction(ref(db, `rooms/${code}`), (room) => {
        if (!room?.settings?.started) return room;
        const curPaused = !!room.settings.paused;
        room.settings.paused = !curPaused;
        if (curPaused) {
          const secs = room.settings.timerSeconds ?? 60;
          room.state = room.state || {};
          room.state.clockEndsAt = Date.now() + secs * 1000;
        }
        return room;
      });
    } catch { setStatus("Pause/resume failed."); }
  };

  const hostResetRoom = async () => {
    const code = leagueCode.trim().toUpperCase();
    if (!isHost || !window.confirm("Reset the room? This clears all picks and rosters.")) return;
    try {
      await runTransaction(ref(db, `rooms/${code}`), (room) => {
        if (!room) return room;
        const tc = room.settings?.teamCount ?? 6;
        const order = getOrderForTeamCount(room.settings?.draftOrder, tc);
        const p = room.players || {};
        Object.keys(p).forEach((id) => { p[id] = { ...p[id], drafted: false, owner: "" }; });
        room.players = p;
        room.teams = TEAM_SLOTS.slice(0, tc).map((name) => ({ name, squadIds: [] }));
        room.picks = [];
        room.settings.started = false;
        room.settings.paused = false;
        room.settings.draftOrder = order;
        room.state = { overallPick: 1, round: 1, pickInRound: 1, onTheClockTeam: order[0], clockEndsAt: 0 };
        room.slots = room.slots || {};
        Object.keys(room.slots).forEach((s) => { room.slots[s] = { ...room.slots[s], ready: false }; });
        return room;
      });
      setStatus("Room reset.");
    } catch { setStatus("Reset failed."); }
  };

  const hostUndoPick = async () => {
    const code = leagueCode.trim().toUpperCase();
    if (!isHost) return;
    try {
      await runTransaction(ref(db, `rooms/${code}`), (room) => {
        if (!room?.settings?.started) return room;
        const pk = room.picks || [];
        if (pk.length === 0) return room;
        const last = pk[pk.length - 1];
        const pid = String(last.playerId);
        if (room.players?.[pid]) room.players[pid] = { ...room.players[pid], drafted: false, owner: "" };
        const tIdx = (room.teams || []).findIndex((t) => t.name === last.team);
        if (tIdx >= 0) {
          const squad = room.teams[tIdx].squadIds || [];
          room.teams[tIdx].squadIds = squad.filter((x) => String(x) !== String(last.playerId));
        }
        pk.pop();
        room.picks = pk;
        const secs = room.settings.timerSeconds ?? 60;
        room.state = room.state || {};
        room.state.overallPick = Math.max(1, (room.state.overallPick ?? 1) - 1);
        room.state.round = last.round;
        room.state.pickInRound = last.pickInRound;
        room.state.onTheClockTeam = last.team;
        room.state.clockEndsAt = Date.now() + secs * 1000;
        return room;
      });
      setStatus("Undid last pick.");
    } catch { setStatus("Undo failed."); }
  };

  const draftPlayer = async (playerId) => {
    const code = leagueCode.trim().toUpperCase();
    if (!started || paused) return setStatus("Draft not running.");
    if (!iAmInSlot) return setStatus("You must claim this slot to draft.");
    if (teamSlot !== expectedTeam) return setStatus(`Waiting for ${slotLabel(expectedTeam)}.`);
    setStatus("Drafting...");
    try {
      await runTransaction(ref(db, `rooms/${code}`), (room) => {
        if (!room?.settings?.started || room.settings?.paused) return room;
        const tc = room.settings.teamCount ?? 6;
        const order = getOrderForTeamCount(room.settings?.draftOrder, tc);
        const r = room.state?.round ?? 1;
        const pir = room.state?.pickInRound ?? 1;
        const expected = currentPickTeamName(order, r, pir);
        if (expected !== teamSlot) return room;
        const pid = String(playerId);
        const pl = room.players?.[pid];
        if (!pl || pl.drafted) return room;
        const tIdx = (room.teams || []).findIndex((t) => t.name === teamSlot);
        if (tIdx < 0) return room;
        const team = room.teams[tIdx];
        const squadIds = team.squadIds || EMPTY_ARR;
        if (squadIds.length >= MAX_SQUAD) return room;
        const roleCounts = { BAT: 0, BOWL: 0, ALR: 0, WK: 0 };
        for (const id of squadIds) {
          const p2 = room.players?.[String(id)];
          if (!p2) continue;
          if (roleCounts[p2.role] !== undefined) roleCounts[p2.role] += 1;
        }
        if ((roleCounts[pl.role] ?? 0) >= (ROLE_LIMITS[pl.role] ?? 0)) return room;
        room.players[pid] = { ...pl, drafted: true, owner: teamSlot };
        room.teams[tIdx] = { ...team, squadIds: [...squadIds, pl.id] };
        room.picks = room.picks || [];
        room.picks.push({
          overall: room.state?.overallPick ?? 1,
          round: r,
          pickInRound: pir,
          team: teamSlot,
          playerId: pl.id,
          ts: Date.now()
        });
        const next = nextPickState(order, r, pir);
        room.state = room.state || {};
        room.state.overallPick = (room.state.overallPick ?? 1) + 1;
        room.state.round = next.round;
        room.state.pickInRound = next.pickInRound;
        room.state.onTheClockTeam = next.onTheClockTeam;
        room.state.clockEndsAt = Date.now() + (room.settings.timerSeconds ?? 60) * 1000;
        return room;
      });
      setStatus("Pick submitted.");
    } catch { setStatus("Draft failed."); }
  };

  const autopickLockRef = useRef(false);
  useEffect(() => {
    if (!joined || !roomData || !isHost || !started || paused || !allowAutoPick) return;
    if (secondsLeft === null || secondsLeft > 0 || autopickLockRef.current) return;
    if (!joined || !roomData || !isHost || !started || paused || !allowAutoPick) return;
    if (secondsLeft === null || secondsLeft > 0 || autopickLockRef.current) return;
    autopickLockRef.current = true;
    const code = leagueCode.trim().toUpperCase();
    const doAuto = async () => {
      try {
        await runTransaction(ref(db, `rooms/${code}`), (room) => {
          if (!room?.settings?.started || room.settings?.paused) return room;
          const tc = room.settings.teamCount ?? 6;
          const order = getOrderForTeamCount(room.settings?.draftOrder, tc);
          const r = room.state?.round ?? 1;
          const pir = room.state?.pickInRound ?? 1;
          const teamName = currentPickTeamName(order, r, pir);
          const tIdx = (room.teams || []).findIndex((t) => t.name === teamName);
          if (tIdx < 0) return room;
          const team = room.teams[tIdx];
          const squadIds = team.squadIds || EMPTY_ARR;
          const roleCounts = { BAT: 0, BOWL: 0, ALR: 0, WK: 0 };
          for (const id of squadIds) {
            const p2 = room.players?.[String(id)];
            if (!p2) continue;
            if (roleCounts[p2.role] !== undefined) roleCounts[p2.role] += 1;
          }
          const deficits = {
            BAT: ROLE_LIMITS.BAT - roleCounts.BAT,
            BOWL: ROLE_LIMITS.BOWL - roleCounts.BOWL,
            ALR: ROLE_LIMITS.ALR - roleCounts.ALR,
            WK: ROLE_LIMITS.WK - roleCounts.WK
          };
          const available = Object.values(room.players || EMPTY_OBJ).filter((p) => !p.drafted);
          if (available.length === 0) return room;
          const rolePriority = Object.keys(deficits).map((role) => ({ role, deficit: deficits[role] }))
            .filter((x) => x.deficit > 0).sort((a, b) => b.deficit - a.deficit);
          let chosen = null;
          for (const rp of rolePriority) {
            const candidates = available.filter((p) => p.role === rp.role).sort((a, b) => a.name.localeCompare(b.name));
            if (candidates.length) { chosen = candidates[0]; break; }
          }
          if (!chosen) {
            const anyValid = available.filter((p) => (roleCounts[p.role] ?? 0) < (ROLE_LIMITS[p.role] ?? 0))
              .sort((a, b) => a.name.localeCompare(b.name));
            if (anyValid.length) chosen = anyValid[0];
          }
          if (!chosen) return room;
          const pid = String(chosen.id);
          room.players[pid] = { ...chosen, drafted: true, owner: teamName };
          room.teams[tIdx] = { ...team, squadIds: [...squadIds, chosen.id] };
          room.picks = room.picks || [];
          room.picks.push({
            overall: room.state?.overallPick ?? 1, round: r, pickInRound: pir, team: teamName,
            playerId: chosen.id, ts: Date.now(), auto: true
          });
          const next = nextPickState(order, r, pir);
          room.state = room.state || {};
          room.state.overallPick = (room.state.overallPick ?? 1) + 1;
          room.state.round = next.round;
          room.state.pickInRound = next.pickInRound;
          room.state.onTheClockTeam = next.onTheClockTeam;
          room.state.clockEndsAt = Date.now() + (room.settings.timerSeconds ?? 60) * 1000;
          return room;
        });
      } finally {
        setTimeout(() => { autopickLockRef.current = false; }, 1500);
      }
    };
    doAuto();
  }, [joined, roomData, isHost, started, paused, allowAutoPick, secondsLeft, leagueCode]);

  const generateRegularSeasonSchedule = (teamNames, weeksRegular = 12) => {
    const teams = (teamNames || []).filter(Boolean).slice(0);
    if (teams.length < 2) return [];
    const list = teams.slice(0);
    if (list.length % 2 === 1) list.push("BYE");
    const n = list.length;
    const rounds = n - 1;
    const fixed = list[0];
    let rotating = list.slice(1);
    const baseRounds = [];
    for (let r = 0; r < rounds; r++) {
      const left = [fixed, ...rotating.slice(0, n / 2 - 1)];
      const right = rotating.slice(n / 2 - 1).slice().reverse();
      const matchups = [];
      for (let i = 0; i < n / 2; i++) {
        const a = left[i], b = right[i];
        if (a === "BYE" || b === "BYE") continue;
        const home = r % 2 === 0 ? a : b;
        const away = r % 2 === 0 ? b : a;
        matchups.push({ home, away });
      }
      baseRounds.push(matchups);
      rotating = [rotating[rotating.length - 1], ...rotating.slice(0, rotating.length - 1)];
    }
    if (baseRounds.length >= weeksRegular) return baseRounds.slice(0, weeksRegular).map((m, i) => ({ week: i + 1, matchups: m }));
    const out = [];
    for (let w = 0; w < weeksRegular; w++) {
      const src = baseRounds[w % baseRounds.length];
      const repeatIndex = Math.floor(w / baseRounds.length);
      const matchups = repeatIndex % 2 === 0 ? src : src.map(({ home, away }) => ({ home: away, away: home }));
      out.push({ week: w + 1, matchups });
    }
    return out;
  };
  const buildPlayoffSchedule = (teamCount) => {
    if (teamCount >= 4) {
      return {
        13: { type: "semifinals", matchups: [{ id: "SF1", homeSeed: 1, awaySeed: 4 }, { id: "SF2", homeSeed: 2, awaySeed: 3 }] },
        14: { type: "final", matchups: [{ id: "F1", homeFrom: "SF1", awayFrom: "SF2" }] }
      };
    }
    return { 14: { type: "final", matchups: [{ id: "F1", homeSeed: 1, awaySeed: 2 }] } };
  };
  // Auto-fill 11 starters from a squad using role-based slot order.
  // Slot order mirrors LineupPage SLOT_DEFINITIONS: wk1, bat1-3, bowl1-3, ar1-2, flex1-2.
  const buildAutoLineup = (squadIds, playerMap) => {
    const SLOTS = [
      { id: "wk1",   eligible: ["WK"] },
      { id: "bat1",  eligible: ["BAT", "WK"] },
      { id: "bat2",  eligible: ["BAT", "WK"] },
      { id: "bat3",  eligible: ["BAT", "WK"] },
      { id: "bowl1", eligible: ["BOWL"] },
      { id: "bowl2", eligible: ["BOWL"] },
      { id: "bowl3", eligible: ["BOWL"] },
      { id: "ar1",   eligible: ["ALR", "ALLROUND"] },
      { id: "ar2",   eligible: ["ALR", "ALLROUND"] },
      { id: "flex1", eligible: ["BAT", "BOWL", "ALR", "ALLROUND", "WK"] },
      { id: "flex2", eligible: ["BAT", "BOWL", "ALR", "ALLROUND", "WK"] },
    ];
    const squad = (squadIds || []).map(id => playerMap[String(id)]).filter(Boolean);
    const used = new Set();
    const starters = [];
    const startersBySlot = {};
    for (const slot of SLOTS) {
      for (const p of squad) {
        if (used.has(String(p.id))) continue;
        const role = p.role === "ALLROUND" ? "ALR" : (p.role || "");
        if (slot.eligible.includes(role) || slot.eligible.includes(p.role)) {
          starters.push(String(p.id));
          startersBySlot[slot.id] = p.id;
          used.add(String(p.id));
          break;
        }
      }
    }
    return { starters, startersBySlot };
  };

  const hostFinalizeDraftStartSeason = async () => {
    const code = leagueCode.trim().toUpperCase();
    if (!code || !isHost) return;
    setSeasonStatus("");
    try {
      const [lgSnap, roomSnap] = await Promise.all([get(ref(db, `leagues/${code}`)), get(ref(db, `rooms/${code}`))]);
      if (!lgSnap.exists() || !roomSnap.exists()) return setSeasonStatus("League or room not found.");
      const lg = lgSnap.val();
      const room = roomSnap.val();
      const tc = room?.settings?.teamCount ?? lg?.settings?.teamCount ?? 6;
      const teamList = (room?.teams || TEAM_SLOTS.slice(0, tc).map((n) => ({ name: n, squadIds: [] }))).slice(0, tc);
      const players = room?.players || EMPTY_OBJ;
      const slots = room?.slots || EMPTY_OBJ;
      const rostersByTeam = {};
      for (const t of teamList) {
        rostersByTeam[t.name] = (t.squadIds || []).map((id) => {
          const p = players[String(id)];
          return p ? { id: p.id, name: p.name, role: p.role } : { id, name: `#${id}`, role: "UNK" };
        });
      }
      const ownersByTeam = {};
      Object.keys(slots).forEach((slotId) => { ownersByTeam[slotId] = slots[slotId]?.claimedBy || ""; });
      const lineupsWeek1 = {};
      teamList.forEach((t) => {
        const { starters, startersBySlot } = buildAutoLineup(t.squadIds, players);
        const bench = (t.squadIds || []).map(String).filter(id => !starters.includes(id));
        lineupsWeek1[t.name] = { starters, bench, startersBySlot, locked: false, lockedAt: 0, lastSavedAt: 0 };
      });
      const regular = generateRegularSeasonSchedule(teamList.map((t) => t.name), 12);
      const playoffs = buildPlayoffSchedule(tc);
      const updatesObj = {
        [`leagues/${code}/phase`]: "season",
        [`leagues/${code}/season/week`]: lg?.season?.week ?? 1,
        [`leagues/${code}/season/teamCount`]: tc,
        [`leagues/${code}/season/ownersByTeam`]: ownersByTeam,
        [`leagues/${code}/season/rostersByTeam`]: rostersByTeam,
        [`leagues/${code}/season/lineups/${lg?.season?.week ?? 1}`]: lineupsWeek1,
        [`leagues/${code}/season/finalizedAt`]: Date.now(),
        [`leagues/${code}/season/config/weeksRegular`]: 12,
        [`leagues/${code}/season/config/weekSemis`]: 13,
        [`leagues/${code}/season/config/weekFinal`]: 14,
        [`leagues/${code}/season/config/playoffTeams`]: tc >= 4 ? 4 : 2
      };
      regular.forEach((w) => { updatesObj[`leagues/${code}/season/schedule/${w.week}`] = { type: "regular", matchups: w.matchups }; });
      Object.keys(playoffs).forEach((wk) => { updatesObj[`leagues/${code}/season/schedule/${wk}`] = playoffs[wk]; });
      updatesObj[`leagues/${code}/season/scheduleGeneratedAt`] = Date.now();
      await update(ref(db), updatesObj);
      setSeasonStatus("Season started! Use the Season tab.");
    } catch (e) {
      console.error(e);
      setSeasonStatus("Finalize failed.");
    }
  };

  const isMyTurn = started && !paused && iAmInSlot && teamSlot === expectedTeam;
  const toggleTeamCollapse = (slotId) => setCollapsedTeams((prev) => ({ ...prev, [slotId]: !prev[slotId] }));
  const isTeamCollapsed = (slotId) => (collapsedTeams[slotId] ?? isMobileNow());

  // ── Auto-start season when draft completes ────────────────────
  const autoFinalizeRef = useRef(false);
  const isDraftComplete = useMemo(() => {
    if (!started || !teams.length) return false;
    return teams.every(t => (t.squadIds || []).length >= MAX_SQUAD);
  }, [started, teams]);

  useEffect(() => {
    if (!isDraftComplete || !isHost || autoFinalizeRef.current) return;
    if (activeLeague?.phase === "season") return; // already done
    autoFinalizeRef.current = true;
    // Small delay so the last pick UI settles before transitioning
    const timer = setTimeout(() => {
      hostFinalizeDraftStartSeason();
    }, 2000);
    return () => clearTimeout(timer);
  }, [isDraftComplete, isHost, activeLeague?.phase]); // eslint-disable-line

  if (!joined) {
    return (
      <div className="panel" style={{ maxWidth: 480, margin: "20px auto", padding: 24 }}>
        <div style={{ fontWeight: 800, fontSize: 20, fontFamily: "'Playfair Display', Georgia, serif", marginBottom: 4 }}>
          Join Draft Room
        </div>
        <div style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 20 }}>
          League: <span style={{ color: "var(--gold)", fontFamily: "monospace", letterSpacing: "0.08em" }}>{leagueCode}</span>
          {" · "}Phase: <strong>{activeLeague?.phase || "draft"}</strong>
        </div>

        <label className="label" style={{ display: "block", marginBottom: 8 }}>Your Name</label>
        <input
          className="search-box"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={effectiveDisplayName || "Enter your name"}
          style={{ marginBottom: 16 }}
          onKeyDown={e => e.key === "Enter" && joinRoom()}
        />

        <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 16 }}>
          <span style={{ color: "var(--gold)" }}>●</span> You'll be automatically assigned to the next open slot.
          {previewRoom && (
            <span style={{ marginLeft: 8 }}>
              {teamCount - Object.values(previewRoom?.slots || {}).filter(s => s?.claimedBy).length} slots remaining.
            </span>
          )}
        </div>

        <button className="primary" style={{ width: "100%" }} onClick={joinRoom}>
          Join Draft Room
        </button>
        {status && (
          <div style={{ marginTop: 12, fontSize: 13, color: status.startsWith("Joined") ? "var(--green)" : "var(--red)" }}>
            {status}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="App">
      {/* Draft complete banner — visible to everyone */}
      {isDraftComplete && (
        <div style={{
          padding: "14px 20px", margin: "12px 0",
          background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.35)",
          borderRadius: "var(--radius)", textAlign: "center",
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--green)", marginBottom: 4 }}>
            🏆 Draft Complete!
          </div>
          <div style={{ fontSize: 13, color: "var(--text-dim)" }}>
            All squads are full. The season is starting automatically — head to <strong style={{ color: "var(--text)" }}>Matchup</strong> or <strong style={{ color: "var(--text)" }}>Set Lineup</strong>.
          </div>
        </div>
      )}

      <div className={`sticky-bar ${isMyTurn ? "myturn" : ""}`}>
        <div className="sticky-row">
          <div>
            On the clock: <b>{slotLabel(expectedTeam)}</b>
            {started ? (paused ? <span className="pill muted">Paused</span> : null) : <span className="pill muted">Not started</span>}
            {isMyTurn ? <span className="pill">Your turn</span> : null}
          </div>
          <div>
            Timer: <b>{started ? (paused ? "Paused" : secondsLeft === null ? "-" : secondsLeft <= 0 ? "0" : secondsLeft) : "-"}</b>
          </div>
        </div>
        <div className="sticky-row small">
          <span>Room: <b>{leagueCode}</b></span>
          <span>You: <b>{displayName || "?"}</b></span>
          <span>Slot: <b>{slotLabel(teamSlot)}</b> {iAmInSlot ? "(claimed)" : "(not claimed by you)"}</span>
          <span>Round <b>{round}</b> Pick <b>{pickInRound}</b> (Overall <b>{overallPick}</b>)</span>
        </div>
      </div>

      <div className="myteam-bar">
        <div className="myteam-left">
          <b>My Team:</b> {slotLabel(teamSlot)} <span style={{ opacity: 0.8 }}>({(myTeam?.squadIds?.length ?? 0)}/{MAX_SQUAD})</span>
        </div>
        <div className="myteam-right">
          <span>BAT {myCounts.BAT}/{ROLE_LIMITS.BAT}</span>
          <span>BOWL {myCounts.BOWL}/{ROLE_LIMITS.BOWL}</span>
          <span>ALR {myCounts.ALR}/{ROLE_LIMITS.ALR}</span>
          <span>WK {myCounts.WK}/{ROLE_LIMITS.WK}</span>
          <span className="needs">Need: {neededRolesSorted.length ? neededRolesSorted.map((x) => `${x.role}(${x.deficit})`).join(", ") : "Balanced"}</span>
        </div>
      </div>

      <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
        <button onClick={toggleReady} disabled={!iAmInSlot}>{slotInfo?.ready ? "Unready" : "Ready Up"}</button>
        <span style={{ opacity: 0.85 }}>Ready: <b>{readyCount}</b> / <b>{teamCount}</b></span>
        <button
          className="mini"
          onClick={() => {
            const url = `${window.location.origin}/join/${leagueCode}`;
            navigator.clipboard?.writeText(url).catch(() => {});
            setStatus("Invite link copied!");
          }}
        >
          Copy Invite Link
        </button>
      </div>

      {isHost ? (
        <div style={{ marginTop: 14, padding: 10, background: "#fff", borderRadius: 10 }}>
          <h3 style={{ margin: "4px 0 10px" }}>Host Controls</h3>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <label>
              Teams:
              <select value={hostTeamCountInput} onChange={(e) => setHostTeamCountInput(Number(e.target.value))} style={{ marginLeft: 6 }} disabled={started}>
                {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => (<option key={n} value={n}>{n}</option>))}
              </select>
              <button style={{ marginLeft: 8 }} disabled={started} onClick={() => hostSetTeamCount(hostTeamCountInput)}>Apply</button>
            </label>
            <label>
              Timer (s):
              <input type="number" value={hostTimerInput} onChange={(e) => setHostTimerInput(Number(e.target.value))} style={{ marginLeft: 6, width: 90 }} />
              <button style={{ marginLeft: 8 }} onClick={() => hostSetTimer(hostTimerInput)}>Apply</button>
            </label>
            <button onClick={hostReshuffleOrder} disabled={started}>Reshuffle Order</button>
            <button onClick={hostStartDraft} disabled={started || claimedCount < teamCount}>Start Draft</button>
            <button onClick={hostTogglePause} disabled={!started}>{paused ? "Resume" : "Pause"}</button>
            <button onClick={hostUndoPick} disabled={!started || picks.length === 0}>Undo Last Pick</button>
            <button onClick={hostResetRoom}>Reset Room</button>
          </div>
          {isDraftComplete && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: "var(--radius-sm)", fontSize: 13, color: "var(--green)" }}>
              ✓ Draft complete — season starting automatically…
            </div>
          )}
          {seasonStatus && !isDraftComplete && (
            <div style={{ marginTop: 10, fontSize: 13, color: seasonStatus.startsWith("Season") ? "var(--green)" : "var(--red)" }}>
              {seasonStatus}
            </div>
          )}
        </div>
      ) : null}

      <div className="container" style={{ marginTop: 16 }}>
        <div className="player-list">
          <h2>Available Players</h2>

          <div className="controls" style={{ gap: 8, flexWrap: "wrap" }}>
            <input className="search-box" placeholder="Search players..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              {ROLE_OPTIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
            <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
              <option value="AZ">A–Z</option>
              <option value="ROLE">Role</option>
              <option value="NEED">Need</option>
            </select>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={needOnly} onChange={(e) => setNeedOnly(e.target.checked)} />
              Need only
            </label>
          </div>

          {filteredPlayers.map((p) => {
            return (
              <div key={p.id} className="player-card">
                <div>
                  <strong>{p.name}</strong> <span style={{ opacity: 0.7 }}>({p.role})</span>
                </div>
                <button onClick={() => draftPlayer(p.id)} disabled={!isMyTurn}>
                  Draft
                </button>
              </div>
            );
          })}
        </div>

        <div className="teams">
          <div className="team-card" style={{ marginBottom: 14 }}>
            <div className="card-head">
              <h3 style={{ margin: 0 }}>Draft History</h3>
              <button className="mini" onClick={() => setHistoryOpen((v) => !v)}>
                {historyOpen ? "Hide" : "Show"}
              </button>
            </div>
            {historyOpen ? (
              <div className="history">
                {history.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>No picks yet.</div>
                ) : (
                  history.map((pk) => {
                    const pl = playersObj[String(pk.playerId)];
                    const teamName = slotLabel(pk.team);
                    return (
                      <div key={`${pk.ts}-${pk.team}-${pk.playerId}`} className="history-row">
                        <span className="history-left">
                          <b>#{pk.overall}</b> {teamName}
                        </span>
                        <span className="history-right">
                          {pl ? pl.name : `#${pk.playerId}`}
                          {pk.auto ? <span className="pill muted">AUTO</span> : null}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div style={{ opacity: 0.75, fontSize: 13 }}>History hidden.</div>
            )}
          </div>

          {teams.map((team) => {
            const { counts, total } = computeTeamCounts(team);
            const isOnClock = team.name === expectedTeam;
            const collapsed = isTeamCollapsed(team.name);

            return (
              <div key={team.name} className={`team-card ${isOnClock ? "current-turn" : ""}`}>
                <div className="card-head">
                  <h3 style={{ margin: 0 }}>
                    {slotLabel(team.name)} ({total}/{MAX_SQUAD}) {isOnClock ? "(on clock)" : ""}
                  </h3>
                  <button className="mini" onClick={() => toggleTeamCollapse(team.name)}>
                    {collapsed ? "Expand" : "Collapse"}
                  </button>
                </div>

                <p style={{ marginTop: 8 }}>
                  BAT: {counts.BAT}/{ROLE_LIMITS.BAT} | BOWL: {counts.BOWL}/{ROLE_LIMITS.BOWL} | ALR: {counts.ALR}/{ROLE_LIMITS.ALR} | WK:{" "}
                  {counts.WK}/{ROLE_LIMITS.WK}
                </p>

                {!collapsed ? (
                  (team.squadIds || EMPTY_ARR).map((id) => {
                    const pl = playerById.get(String(id)) || playersObj[String(id)];
                    return (
                      <div key={`${team.name}-${id}`} className="player-card drafted">
                        {pl ? `${pl.name} (${pl.role})` : `Player ${id}`}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ opacity: 0.75, fontSize: 13 }}>Tap expand to view roster.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {status ? (
        <div style={{ padding: 12, opacity: 0.9 }}>{status}</div>
      ) : null}
    </div>
  );
}
